import moment from "moment";
import { MONTH_LABELS, WEEK_LABELS } from "../Settings/constants/index";

/* The function below takes your number and number of decimal places to add a decimal place.
Then it removes any number after the decimal place and adds abbreviated markers for k, m, b, t.
e.g 400,000 is now 400k
*/
const abbreviateNumber = (number, decimalPlaces) => {
  const abbrev = ["k", "m", "b", "t"];
  let size;
  let roundedNumber;
  // 2 decimal places => 100, 3 => 1000, etc
  const poweredNumber = Math.pow(10, decimalPlaces);
  for (let i = abbrev.length - 1; i >= 0; --i) {
    size = Math.pow(10, (i + 1) * 3);
    if (size <= number) {
      roundedNumber = Math.round(number * poweredNumber / size) / poweredNumber;
      if (roundedNumber === 1000 && i < abbrev.length - 1) {
        roundedNumber = 1;
        i += 1;
      }
      roundedNumber += abbrev[i];
      break;
    }
  }
  return roundedNumber;
};

/**
 * Format amount to string £x,xxx.xx
 * @param {number} amount - number
 * @returns {string} formatted number
 */
export const formatAmount = (amount, withCurrency = true, includeDecimal = true, shouldAbbreviateNumber = false) => {
  let number = amount;
  let negativeNumber = number < 0;
  let formattedNumber;
  let valueArray;
  if (typeof number !== "number") {
    return false;
  }
  if (negativeNumber) {
    number = Math.abs(number);
  }
  if (includeDecimal) {
    number = (Math.round(number * 100) / 100).toFixed(2);
  } else {
    number = Math.round(number * 100) / 100;
  }
  valueArray = number.toString().split(".");

  // Split to add comma to whole number, not decimals
  if (valueArray[0].length >= 4) {
    valueArray[0] = valueArray[0].replace(/(\d)(?=(\d{3})+$)/g, "$1,");
  }
  formattedNumber = valueArray.join(".");
  if (shouldAbbreviateNumber) {
    formattedNumber = abbreviateNumber(parseInt(number), 2);
  }
  if (withCurrency) {
    formattedNumber = "£" + formattedNumber;
  }
  if (negativeNumber) {
    formattedNumber = "-" + formattedNumber;
  }
  return formattedNumber;
};
export const formatDate = date => {
  const dateObj = moment.utc(date);
  return `${dateObj.date()} ${MONTH_LABELS[dateObj.month()]} ${dateObj.year()}`;
};
export const formatDateWithDay = date => {
  const dateObj = moment.utc(date);
  return `${WEEK_LABELS[dateObj.day()]}, ${dateObj.date()} ${MONTH_LABELS[dateObj.month()]}`;
};
export const formatTime = date => {
  const addZero = i => i < 10 ? "0" + i : i;
  const dateObj = new Date(date);
  const h = addZero(dateObj.getUTCHours());
  const m = addZero(dateObj.getUTCMinutes());
  return `${h}:${m}`;
};

/**
 * Format duration of time from number of minutes to number of hours and minutes
 * @param {number} duration - number of minutes
 * @param {string} hoursText - hours text e.g. 'hour(s)'
 * @param {string} minutesText - minutes text e.g. 'minute(s)'
 * @returns {string} formatted string
 */
export const formatDuration = (duration, hoursText = "hour(s)", minutesText = "minute(s)") => {
  if (typeof duration !== "number" || isNaN(duration) || typeof duration === "undefined" || duration === null || !Number.isInteger(duration)) {
    return "";
  }
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (minutes === 0) {
    return `${hours} ${hoursText}`;
  }
  return `${hours} ${hoursText} ${minutes} ${minutesText}`;
};
export const replaceToken = (string, token, value) => {
  const regex = new RegExp(token, "g");
  return string.replace(regex, value);
};
export const sanitizeString = string => {
  const map = {
    "[": "",
    "]": "",
    "<": "",
    ">": "",
    "{": "",
    "}": "",
    "/": "",
    "`": "",
    "¬": ""
  };
  const reg = /[\[\]<>{}`¬/]/g;
  return string.replace(reg, match => map[match]);
};

/**
 * Format URL string to sentence case 'abc-def-ghi => Abc def ghi'
 * @param {string} string - string to format
 * @returns {string} formatted string
 */
export const formatSentenceCase = string => {
  let newString = string;

  // Replace any hyphens with an empty space and capitalize the first letter
  newString = newString.replace(/-/g, " ");
  newString = newString.charAt(0).toUpperCase() + newString.slice(1);
  return newString;
};

/**
 * Hyphenate string '123 456 789 => 123-456-789'
 * @param {string} string - string to format
 * @returns {string} formatted string
 */
export const hyphenateString = string => {
  if (typeof string !== "string" || string === null) {
    return null;
  }
  return string.split(" ").join("-");
};